import { useState, useEffect, useRef } from 'react';
import {
  User,
  Building2,
  MapPin,
  CreditCard,
  Bell,
  Shield,
  Palette,
  Printer,
  Users,
  ChevronRight,
  Save,
  Upload,
  Settings as SettingsIcon,
  Lock,
  Eye,
  EyeOff,
  Check,
  AlertCircle,
  Loader2,
  Plus,
  Trash2,
  Edit2,
  DollarSign,
  Banknote,
  Star,
  ArrowUp,
  ArrowDown,
  MoreHorizontal,
  Phone,
  Mail,
  Building,
  ExternalLink,
  AlertTriangle,
  Database,
  KeyRound,
  Sparkles,
  RefreshCw,
  Smartphone,
  Copy,
  CheckCircle2,
  Monitor,
  Tablet,
  Globe,
  LogOut,
  Clock,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { JAMAICA_PARISHES } from '@/lib/jamaica';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { useTheme } from '@/components/theme-provider';
import { usePaymentTypes } from '@/hooks/usePaymentTypes';
import { useLocations, type Location, type FohStockWarningBehavior } from '@/hooks/useLocations';
import { useTeam, type TeamMember } from '@/hooks/useTeam';
import { useAppearanceSettings } from '@/hooks/useAppearanceSettings';
import { SYSTEM_FONTS, type SystemFont, useFeatureFlags } from '@/hooks/useFeatureFlags';
import { BillingSection } from '@/components/settings/BillingSection';
import { ApiKeysSection } from '@/components/settings/ApiKeysSection';
import { AccountDangerZone } from '@/components/settings/AccountDangerZone';
import { useTwoFactor } from '@/hooks/useTwoFactor';
import { useActiveSessions } from '@/hooks/useActiveSessions';
import { usePrinters } from '@/hooks/usePrinters';
import { useOwner2FAApproval } from '@/hooks/useOwner2FAApproval';
import { useNotificationPreferences } from '@/hooks/useNotificationPreferences';
import { useNavigate } from 'react-router-dom';

type SettingsSection =
  | 'profile'
  | 'organization'
  | 'locations'
  | 'payments'
  | 'billing'
  | 'notifications'
  | 'security'
  | 'appearance'
  | 'hardware'
  | 'team';

const settingsSections = [
  { id: 'profile', label: 'Profile', icon: User, description: 'Your personal account settings' },
  { id: 'organization', label: 'Organization', icon: Building2, description: 'Business details and branding' },
  { id: 'locations', label: 'Locations', icon: MapPin, description: 'Manage store locations' },
  { id: 'team', label: 'Team', icon: Users, description: 'Employee access and permissions' },
  { id: 'payments', label: 'Payments', icon: CreditCard, description: 'Payment processing settings' },
  { id: 'billing', label: 'Billing', icon: DollarSign, description: 'Subscription and licenses', ownerAndAbove: true },
  { id: 'notifications', label: 'Notifications', icon: Bell, description: 'Alert preferences' },
  { id: 'security', label: 'Security', icon: Shield, description: 'Security and privacy settings' },
  { id: 'appearance', label: 'Appearance', icon: Palette, description: 'Theme and display options', adminOnly: true },
  { id: 'hardware', label: 'Hardware', icon: Printer, description: 'Printers and devices' },
] as const;

interface Toast {
  type: 'success' | 'error';
  message: string;
}

const ROLE_COLORS: Record<string, string> = {
  superadmin: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
  primary_admin: 'bg-rose-500/10 text-rose-600 border-rose-500/20',
  owner: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  admin: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  manager: 'bg-green-500/10 text-green-600 border-green-500/20',
  employee: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
};

const ROLE_LABELS: Record<string, string> = {
  superadmin: 'Super Admin',
  primary_admin: 'Business Owner',
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  employee: 'Cashier',
};

// Security Section Component with 2FA
function SecuritySection() {
  const {
    status: twoFactorStatus,
    isLoading: twoFactorLoading,
    setupData,
    backupCodes,
    initializeSetup,
    verifyAndEnable,
    disable,
    regenerateBackupCodes,
    trustedDevices,
    removeTrustedDevice,
    updateTriggerSettings,
  } = useTwoFactor();

  const {
    sessions,
    isLoading: sessionsLoading,
    revokeSession,
    revokeAllOtherSessions,
    formatSession,
    getRelativeTime,
  } = useActiveSessions();

  const { requireApproval, owner2FAEnabled } = useOwner2FAApproval();

  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showBackupCodesModal, setShowBackupCodesModal] = useState(false);
  const [showSessionsModal, setShowSessionsModal] = useState(false);
  const [showTrustedDevicesModal, setShowTrustedDevicesModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [setupStep, setSetupStep] = useState<'qr' | 'verify' | 'backup'>('qr');
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [copiedBackupCodes, setCopiedBackupCodes] = useState(false);
  const [selectedTrigger, setSelectedTrigger] = useState<string>(twoFactorStatus.twoFactorTrigger);
  const [inactivityDays, setInactivityDays] = useState(twoFactorStatus.inactivityDays);

  // Update local state when status loads
  useEffect(() => {
    setSelectedTrigger(twoFactorStatus.twoFactorTrigger);
    setInactivityDays(twoFactorStatus.inactivityDays);
  }, [twoFactorStatus.twoFactorTrigger, twoFactorStatus.inactivityDays]);

  const handleUpdateTrigger = async (trigger: string) => {
    setSelectedTrigger(trigger);
    try {
      await updateTriggerSettings.mutateAsync({
        trigger: trigger as 'every_login' | 'new_device' | 'after_inactivity',
        inactivityDays: inactivityDays,
      });
    } catch (error) {
      console.error('Failed to update trigger settings:', error);
      setSelectedTrigger(twoFactorStatus.twoFactorTrigger);
    }
  };

  const handleUpdateInactivityDays = async (days: number) => {
    setInactivityDays(days);
    try {
      await updateTriggerSettings.mutateAsync({
        trigger: selectedTrigger as 'every_login' | 'new_device' | 'after_inactivity',
        inactivityDays: days,
      });
    } catch (error) {
      console.error('Failed to update inactivity days:', error);
      setInactivityDays(twoFactorStatus.inactivityDays);
    }
  };

  const getDeviceIcon = (deviceType: string) => {
    switch (deviceType) {
      case 'mobile':
        return Smartphone;
      case 'tablet':
        return Tablet;
      case 'desktop':
      default:
        return Monitor;
    }
  };

  const handleStartSetup = async () => {
    setIsSettingUp(true);
    try {
      await initializeSetup();
      setSetupStep('qr');
      setShowSetupModal(true);
    } catch (error) {
      console.error('Failed to initialize 2FA setup:', error);
    } finally {
      setIsSettingUp(false);
    }
  };

  const handleVerifyCode = async () => {
    if (verificationCode.length !== 6) return;
    
    try {
      const codes = await verifyAndEnable.mutateAsync(verificationCode);
      if (codes) {
        setSetupStep('backup');
      }
    } catch (error) {
      console.error('Failed to verify code:', error);
    }
  };

  const handleDisable2FA = async () => {
    try {
      // Check if owner 2FA approval is required
      if (owner2FAEnabled) {
        const approved = await requireApproval({
          actionType: 'disable_2fa',
          metadata: {
            action: 'disable_user_2fa',
          },
          description: 'Disable Two-Factor Authentication for your account',
        });

        if (!approved) {
          console.log('Owner 2FA approval denied for disabling 2FA');
          return;
        }
      }

      await disable.mutateAsync(disablePassword);
      setShowDisableModal(false);
      setDisablePassword('');
    } catch (error) {
      console.error('Failed to disable 2FA:', error);
    }
  };

  const handleCopyBackupCodes = () => {
    if (backupCodes) {
      navigator.clipboard.writeText(backupCodes.join('\n'));
      setCopiedBackupCodes(true);
      setTimeout(() => setCopiedBackupCodes(false), 2000);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    try {
      await regenerateBackupCodes.mutateAsync();
    } catch (error) {
      console.error('Failed to regenerate backup codes:', error);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">Security Settings</h3>
        <p className="text-sm text-muted-foreground">
          Manage your security preferences and sessions
        </p>
      </div>

      <div className="space-y-4">
        {/* Two-Factor Authentication */}
        <div className="p-4 rounded-xl border bg-muted/30">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className={cn(
                'p-2 rounded-lg',
                twoFactorStatus.enabled
                  ? 'bg-green-500/10'
                  : 'bg-muted'
              )}>
                <Smartphone className={cn(
                  'h-5 w-5',
                  twoFactorStatus.enabled ? 'text-green-600' : 'text-muted-foreground'
                )} />
              </div>
              <div>
                <h4 className="font-medium">Two-Factor Authentication</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Add an extra layer of security to your account using an authenticator app
                </p>
                {twoFactorStatus.enabled && (
                  <div className="mt-2 flex items-center gap-4 text-xs">
                    <span className="flex items-center gap-1 text-green-600">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Enabled
                    </span>
                    <span className="text-muted-foreground">
                      {twoFactorStatus.backupCodesRemaining} backup codes remaining
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {twoFactorStatus.enabled ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowBackupCodesModal(true)}
                  >
                    Backup Codes
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setShowDisableModal(true)}
                  >
                    Disable
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  onClick={handleStartSetup}
                  disabled={isSettingUp || twoFactorLoading}
                >
                  {isSettingUp && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Enable 2FA
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* 2FA Preferences - Only shown when 2FA is enabled */}
        {twoFactorStatus.enabled && (
          <div className="p-4 rounded-xl border bg-muted/30 space-y-4">
            <div>
              <h4 className="font-medium">When to Require 2FA</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Choose when you want to be prompted for your 2FA code
              </p>
            </div>

            <div className="space-y-3">
              <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors">
                <input
                  type="radio"
                  name="2fa-trigger"
                  value="every_login"
                  checked={selectedTrigger === 'every_login'}
                  onChange={(e) => handleUpdateTrigger(e.target.value)}
                  className="mt-1"
                />
                <div>
                  <p className="font-medium text-sm">Every login</p>
                  <p className="text-xs text-muted-foreground">
                    Always require 2FA code when logging in (most secure)
                  </p>
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors">
                <input
                  type="radio"
                  name="2fa-trigger"
                  value="new_device"
                  checked={selectedTrigger === 'new_device'}
                  onChange={(e) => handleUpdateTrigger(e.target.value)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <p className="font-medium text-sm">New device only</p>
                  <p className="text-xs text-muted-foreground">
                    Only require 2FA when logging in from an unrecognized device
                  </p>
                  {selectedTrigger === 'new_device' && trustedDevices.length > 0 && (
                    <Button
                      variant="link"
                      size="sm"
                      className="h-auto p-0 mt-2 text-xs"
                      onClick={() => setShowTrustedDevicesModal(true)}
                    >
                      Manage {trustedDevices.length} trusted device{trustedDevices.length !== 1 ? 's' : ''}
                    </Button>
                  )}
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors">
                <input
                  type="radio"
                  name="2fa-trigger"
                  value="after_inactivity"
                  checked={selectedTrigger === 'after_inactivity'}
                  onChange={(e) => handleUpdateTrigger(e.target.value)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <p className="font-medium text-sm">After inactivity</p>
                  <p className="text-xs text-muted-foreground">
                    Require 2FA if you haven&apos;t verified in a while
                  </p>
                  {selectedTrigger === 'after_inactivity' && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Require after</span>
                      <Select
                        value={String(inactivityDays)}
                        onValueChange={(value) => handleUpdateInactivityDays(Number(value))}
                      >
                        <SelectTrigger className="w-24 h-7 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 day</SelectItem>
                          <SelectItem value="3">3 days</SelectItem>
                          <SelectItem value="7">7 days</SelectItem>
                          <SelectItem value="14">14 days</SelectItem>
                          <SelectItem value="30">30 days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </label>
            </div>
          </div>
        )}

        {/* Active Sessions */}
        <div className="p-4 rounded-xl border bg-muted/30">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-muted">
                <Monitor className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <h4 className="font-medium">Active Sessions</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Manage devices where you&apos;re currently logged in
                </p>
                {sessions.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {sessions.length} active session{sessions.length !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSessionsModal(true)}
              disabled={sessionsLoading}
            >
              {sessionsLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'View Sessions'
              )}
            </Button>
          </div>
        </div>

        {/* Login History */}
        <div className="p-4 rounded-xl border bg-muted/30">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="font-medium">Login History</h4>
              <p className="text-sm text-muted-foreground mt-1">
                View recent login activity on your account
              </p>
            </div>
            <Button variant="outline" size="sm">
              View History
            </Button>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <AccountDangerZone />

      {/* 2FA Setup Modal */}
      <ResponsiveModal open={showSetupModal} onOpenChange={setShowSetupModal}>
        <ResponsiveModalContent className="max-w-md">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>
              {setupStep === 'qr' && 'Set Up Two-Factor Authentication'}
              {setupStep === 'verify' && 'Verify Your Code'}
              {setupStep === 'backup' && 'Save Your Backup Codes'}
            </ResponsiveModalTitle>
            <ResponsiveModalDescription>
              {setupStep === 'qr' && 'Scan this QR code with your authenticator app'}
              {setupStep === 'verify' && 'Enter the 6-digit code from your authenticator app'}
              {setupStep === 'backup' && 'Save these codes in a safe place. You can use them if you lose access to your authenticator.'}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody className="space-y-4">
            {setupStep === 'qr' && setupData && (
              <div className="space-y-4">
                <div className="flex justify-center p-4 bg-white rounded-lg">
                  <img
                    src={setupData.qrCodeDataUrl}
                    alt="2FA QR Code"
                    className="w-48 h-48"
                  />
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-2">
                    Or enter this code manually:
                  </p>
                  <code className="px-3 py-1.5 bg-muted rounded text-sm font-mono">
                    {setupData.secret}
                  </code>
                </div>
              </div>
            )}

            {setupStep === 'verify' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Verification Code</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    className="text-center text-2xl tracking-widest font-mono"
                  />
                </div>
              </div>
            )}

            {setupStep === 'backup' && backupCodes && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2 p-4 bg-muted rounded-lg">
                  {backupCodes.map((code, index) => (
                    <code key={index} className="text-sm font-mono text-center py-1">
                      {code}
                    </code>
                  ))}
                </div>
                <Button
                  variant="outline"
                  className="w-full gap-2"
                  onClick={handleCopyBackupCodes}
                >
                  {copiedBackupCodes ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  {copiedBackupCodes ? 'Copied!' : 'Copy All Codes'}
                </Button>
                <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />
                    Each backup code can only be used once. Store them securely.
                  </p>
                </div>
              </div>
            )}
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            {setupStep === 'qr' && (
              <>
                <Button variant="outline" onClick={() => setShowSetupModal(false)}>
                  Cancel
                </Button>
                <Button onClick={() => setSetupStep('verify')}>
                  Continue
                </Button>
              </>
            )}
            {setupStep === 'verify' && (
              <>
                <Button variant="outline" onClick={() => setSetupStep('qr')}>
                  Back
                </Button>
                <Button
                  onClick={handleVerifyCode}
                  disabled={verificationCode.length !== 6 || verifyAndEnable.isPending}
                >
                  {verifyAndEnable.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Verify
                </Button>
              </>
            )}
            {setupStep === 'backup' && (
              <Button
                className="w-full"
                onClick={() => {
                  setShowSetupModal(false);
                  setSetupStep('qr');
                  setVerificationCode('');
                }}
              >
                Done
              </Button>
            )}
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Disable 2FA Modal */}
      <ResponsiveModal open={showDisableModal} onOpenChange={setShowDisableModal}>
        <ResponsiveModalContent className="max-w-sm">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Disable Two-Factor Authentication</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              This will remove the extra layer of security from your account.
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody className="space-y-4">
            <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
              <p className="text-xs text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />
                Your account will be less secure without 2FA enabled.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Enter your password to confirm</Label>
              <Input
                type="password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                placeholder="Your password"
              />
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setShowDisableModal(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDisable2FA}
              disabled={!disablePassword || disable.isPending}
            >
              {disable.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Disable 2FA
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Backup Codes Modal */}
      <ResponsiveModal open={showBackupCodesModal} onOpenChange={setShowBackupCodesModal}>
        <ResponsiveModalContent className="max-w-md">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Backup Codes</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Use these codes if you lose access to your authenticator app
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody className="space-y-4">
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground">
                You have <strong>{twoFactorStatus.backupCodesRemaining}</strong> backup codes remaining
              </p>
            </div>
            <Button
              variant="outline"
              className="w-full gap-2"
              onClick={handleRegenerateBackupCodes}
              disabled={regenerateBackupCodes.isPending}
            >
              {regenerateBackupCodes.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Regenerate Backup Codes
            </Button>
            {backupCodes && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2 p-4 bg-muted rounded-lg">
                  {backupCodes.map((code, index) => (
                    <code key={index} className="text-sm font-mono text-center py-1">
                      {code}
                    </code>
                  ))}
                </div>
                <Button
                  variant="outline"
                  className="w-full gap-2"
                  onClick={handleCopyBackupCodes}
                >
                  {copiedBackupCodes ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  {copiedBackupCodes ? 'Copied!' : 'Copy All Codes'}
                </Button>
              </div>
            )}
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button className="w-full" onClick={() => setShowBackupCodesModal(false)}>
              Done
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Active Sessions Modal */}
      <ResponsiveModal open={showSessionsModal} onOpenChange={setShowSessionsModal}>
        <ResponsiveModalContent className="max-w-lg">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Active Sessions</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Manage devices where you are currently logged in. You can sign out of any session.
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody className="space-y-4">
            {sessionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : sessions.length === 0 ? (
              <div className="text-center py-8">
                <Monitor className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-sm text-muted-foreground">
                  No active sessions found
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {sessions.map((session) => {
                  const DeviceIcon = getDeviceIcon(session.device_type);
                  return (
                    <div
                      key={session.id}
                      className={cn(
                        'p-4 rounded-lg border',
                        session.is_current
                          ? 'bg-primary/5 border-primary/20'
                          : 'bg-muted/30'
                      )}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div
                            className={cn(
                              'p-2 rounded-lg',
                              session.is_current ? 'bg-primary/10' : 'bg-muted'
                            )}
                          >
                            <DeviceIcon
                              className={cn(
                                'h-5 w-5',
                                session.is_current
                                  ? 'text-primary'
                                  : 'text-muted-foreground'
                              )}
                            />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm truncate">
                                {formatSession(session)}
                              </p>
                              {session.is_current && (
                                <Badge variant="default" className="text-xs">
                                  Current
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                              {session.ip_address && (
                                <span className="flex items-center gap-1">
                                  <Globe className="h-3 w-3" />
                                  {session.ip_address}
                                </span>
                              )}
                              {(session.city || session.country) && (
                                <span>
                                  {[session.city, session.country]
                                    .filter(Boolean)
                                    .join(', ')}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              Last active {getRelativeTime(session.last_active_at)}
                            </div>
                          </div>
                        </div>
                        {!session.is_current && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => revokeSession.mutate(session.id)}
                            disabled={revokeSession.isPending}
                          >
                            {revokeSession.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <LogOut className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {sessions.filter((s) => !s.is_current).length > 0 && (
              <Button
                variant="outline"
                className="w-full gap-2 text-destructive hover:text-destructive"
                onClick={() => revokeAllOtherSessions.mutate()}
                disabled={revokeAllOtherSessions.isPending}
              >
                {revokeAllOtherSessions.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <LogOut className="h-4 w-4" />
                )}
                Sign out of all other sessions
              </Button>
            )}
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button className="w-full" onClick={() => setShowSessionsModal(false)}>
              Done
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Trusted Devices Modal */}
      <ResponsiveModal open={showTrustedDevicesModal} onOpenChange={setShowTrustedDevicesModal}>
        <ResponsiveModalContent className="max-w-lg">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Trusted Devices</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Devices you have trusted will not require 2FA verification when logging in.
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody className="space-y-4">
            {trustedDevices.length === 0 ? (
              <div className="text-center py-8">
                <Monitor className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-sm text-muted-foreground">
                  No trusted devices yet. Devices will appear here when you choose to trust them during login.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {trustedDevices.map((device) => {
                  const DeviceIcon = getDeviceIcon(
                    device.os?.toLowerCase().includes('android') || device.os?.toLowerCase().includes('ios')
                      ? 'mobile'
                      : device.os?.toLowerCase().includes('ipad')
                      ? 'tablet'
                      : 'desktop'
                  );
                  const deviceDisplay = [device.browser, device.os].filter(Boolean).join(' on ') || 'Unknown device';

                  return (
                    <div
                      key={device.id}
                      className="p-4 rounded-lg border bg-muted/30"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-muted">
                            <DeviceIcon className="h-5 w-5 text-muted-foreground" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">
                              {deviceDisplay}
                            </p>
                            <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              Trusted {getRelativeTime(device.trustedAt)}
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              Last used {getRelativeTime(device.lastUsedAt)}
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => removeTrustedDevice.mutate(device.id)}
                          disabled={removeTrustedDevice.isPending}
                        >
                          {removeTrustedDevice.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button className="w-full" onClick={() => setShowTrustedDevicesModal(false)}>
              Done
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>
    </div>
  );
}

export default function Settings() {
  const { profile, organization, user, updateProfile, updateOrganization, loading: authLoading, isSuperadmin, isAdmin, isOwner } = useAuth();
  const isDev = typeof window !== 'undefined'
    && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const canSeedData = isDev || isSuperadmin || isAdmin;
  const { theme, setTheme, accentColor: currentAccentColor, setAccentColor: applyAccentColor } = useTheme();
  const { flags } = useFeatureFlags();
  const {
    printers,
    defaultReceiptPrinter,
    defaultKitchenPrinter,
    isDesktopApp,
    isLoading: printersLoading,
    refreshPrinters,
    setReceiptPrinter,
    setKitchenPrinter,
    printTestPage,
    openCashDrawer,
  } = usePrinters();
  const navigate = useNavigate();
  const {
    preferences: notificationPreferences,
    isLoading: notificationPrefsLoading,
    isSaving: notificationPrefsSaving,
    setPreference: setNotificationPreference,
  } = useNotificationPreferences();
  const [activeSection, setActiveSection] = useState<SettingsSection>('profile');
  const [toast, setToast] = useState<Toast | null>(null);
  const [saving, setSaving] = useState(false);

  // Profile state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // PIN state
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [savingPin, setSavingPin] = useState(false);
  const [hasExistingPin, setHasExistingPin] = useState(false);
  const [pinResetPassword, setPinResetPassword] = useState('');
  const [pinResetVerified, setPinResetVerified] = useState(false);
  const [verifyingPassword, setVerifyingPassword] = useState(false);

  // Organization state
  const [orgName, setOrgName] = useState('');
  const [orgSlug, setOrgSlug] = useState('');
  const [pendingSlug, setPendingSlug] = useState(''); // For confirmation dialog
  const [slugChangeDialogOpen, setSlugChangeDialogOpen] = useState(false);
  const [timezone, setTimezone] = useState('America/Jamaica');
  const [currency, setCurrency] = useState('JMD');
  const [taxRate, setTaxRate] = useState('15');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // AI Settings state
  const [aiApiKey, setAiApiKey] = useState('');
  const [showAiApiKey, setShowAiApiKey] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [savingAiSettings, setSavingAiSettings] = useState(false);

  // Appearance state
  const [selectedTheme, setSelectedTheme] = useState<'light' | 'dark' | 'system'>('system');
  const [accentColor, setAccentColor] = useState(currentAccentColor);
  const [selectedFont, setSelectedFont] = useState<SystemFont>('inter');
  const [appearanceScope, setAppearanceScope] = useState<'organization' | 'location'>('organization');
  const [selectedAppearanceLocationId, setSelectedAppearanceLocationId] = useState<string | null>(null);
  const {
    effectiveAppearance,
    hasLocationOverride,
    updateOrgAppearance,
    updateLocationAppearance,
    clearLocationAppearance,
    canEditOrgSettings,
    isLoading: appearanceLoading,
  } = useAppearanceSettings();
  const appearanceInitialized = useRef(false);

  // Payment types
  const { 
    paymentTypes, 
    isLoading: paymentTypesLoading,
    createPaymentType,
    updatePaymentType,
    deletePaymentType,
    setPrimaryPaymentType,
    updateSortOrders,
  } = usePaymentTypes();
  
  const [paymentTypeDialogOpen, setPaymentTypeDialogOpen] = useState(false);
  const [editingPaymentType, setEditingPaymentType] = useState<typeof paymentTypes[0] | null>(null);
  const [paymentTypeForm, setPaymentTypeForm] = useState({
    name: '',
    code: '',
    is_cash_equivalent: false,
  });
  const [savingPaymentType, setSavingPaymentType] = useState(false);

  // Locations
  const { 
    locations, 
    isLoading: locationsLoading, 
    createLocation,
    updateLocation,
    updateLocationSettings,
    deleteLocation
  } = useLocations();
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [locationForm, setLocationForm] = useState({
    name: '',
    phone: '',
    email: '',
    address_line1: '',
    address_city: '',
    address_parish: '',
  });
  const [locationFormError, setLocationFormError] = useState<string | null>(null);
  const [deleteLocationDialogOpen, setDeleteLocationDialogOpen] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState<Location | null>(null);
  const [seedingLocationId, setSeedingLocationId] = useState<string | null>(null);

  // Team
  const { teamMembers, isLoading: teamLoading, toggleStaffActive } = useTeam();
  const [togglingActive, setTogglingActive] = useState<string | null>(null);

  // Initialize state from props
  useEffect(() => {
    if (profile) {
      setFirstName(profile.first_name || '');
      setLastName(profile.last_name || '');
    }
  }, [profile]);

  // Check if user has existing PIN
  useEffect(() => {
    const checkExistingPin = async () => {
      if (!user?.id) return;

      const { data } = await supabase
        .from('profiles')
        .select('pin_hash')
        .eq('id', user.id)
        .single();

      setHasExistingPin(!!data?.pin_hash);
    };

    checkExistingPin();
  }, [user?.id]);

  useEffect(() => {
    if (organization) {
      setOrgName(organization.name || '');
      setOrgSlug(organization.slug || '');
      setLogoUrl(organization.logo_url || null);
      // Load AI settings from organization settings
      const settings = organization.settings || {};
      setAiEnabled(settings.ai_upsell_enabled || false);
      setAiApiKey(settings.openai_api_key || '');
    }
  }, [organization]);

  // Initialize appearance state from effective settings (only once on first load)
  useEffect(() => {
    if (!appearanceLoading && !appearanceInitialized.current) {
      setSelectedTheme(effectiveAppearance.theme || 'system');
      setAccentColor(effectiveAppearance.accentColor || '#f97316');
      setSelectedFont(effectiveAppearance.font || 'inter');
      appearanceInitialized.current = true;
    }
  }, [effectiveAppearance, appearanceLoading]);

  useEffect(() => {
    // Fetch organization settings
    const fetchOrgSettings = async () => {
      if (!organization?.id) return;
      
      const { data } = await supabase
        .from('organizations')
        .select('timezone, currency, tax_settings')
        .eq('id', organization.id)
        .single();

      if (data) {
        setTimezone(data.timezone || 'America/Jamaica');
        setCurrency(data.currency || 'JMD');
        setTaxRate(data.tax_settings?.default_rate?.toString() || '15');
      }
    };

    fetchOrgSettings();
  }, [organization?.id]);

  useEffect(() => {
    setSelectedTheme(theme);
  }, [theme]);

  useEffect(() => {
    setAccentColor(currentAccentColor);
  }, [currentAccentColor]);

  // Auto-dismiss toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
  };

  const userInitials = `${profile?.first_name?.[0] ?? ''}${profile?.last_name?.[0] ?? ''}`.toUpperCase();

  // Save profile
  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await updateProfile({
        first_name: firstName,
        last_name: lastName,
      });
      showToast('success', 'Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      showToast('error', 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  // Change password
  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      showToast('error', 'New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      showToast('error', 'Password must be at least 8 characters');
      return;
    }

    setChangingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      showToast('success', 'Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      console.error('Error changing password:', error);
      showToast('error', error.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // Helper function to hash PIN using bcrypt via Supabase RPC
  const hashPin = async (pin: string): Promise<string> => {
    const { data, error } = await supabase.rpc('hash_pin', { p_pin: pin });
    if (error) throw new Error('Failed to hash PIN');
    return data as string;
  };

  // Save or update PIN
  const handleSavePin = async () => {
    if (newPin !== confirmPin) {
      showToast('error', 'PINs do not match');
      return;
    }

    if (newPin.length !== 6 || !/^\d{6}$/.test(newPin)) {
      showToast('error', 'PIN must be exactly 6 digits');
      return;
    }

    if (!user?.id) {
      showToast('error', 'User not found');
      return;
    }

    setSavingPin(true);
    try {
      const pinHash = await hashPin(newPin);

      const { error } = await supabase
        .from('profiles')
        .update({ pin_hash: pinHash })
        .eq('id', user.id);

      if (error) throw error;

      showToast('success', hasExistingPin ? 'PIN updated successfully' : 'PIN set successfully');
      setNewPin('');
      setConfirmPin('');
      setHasExistingPin(true);
    } catch (error: any) {
      console.error('Error saving PIN:', error);
      showToast('error', error.message || 'Failed to save PIN');
    } finally {
      setSavingPin(false);
    }
  };

  // Verify password for PIN reset
  const handleVerifyPasswordForPinReset = async () => {
    if (!user?.email || !pinResetPassword) return;

    setVerifyingPassword(true);
    try {
      // Try to sign in with the password to verify it's correct
      const { error } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: pinResetPassword,
      });

      if (error) {
        showToast('error', 'Incorrect password');
        return;
      }

      // Password verified, allow PIN reset
      setPinResetVerified(true);
      showToast('success', 'Password verified. You can now reset your PIN.');
    } catch (error: any) {
      showToast('error', error.message || 'Failed to verify password');
    } finally {
      setVerifyingPassword(false);
    }
  };

  // Remove PIN
  const handleRemovePin = async () => {
    if (!user?.id) return;

    setSavingPin(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ pin_hash: null })
        .eq('id', user.id);

      if (error) throw error;

      showToast('success', 'PIN removed');
      setHasExistingPin(false);
    } catch (error: any) {
      console.error('Error removing PIN:', error);
      showToast('error', error.message || 'Failed to remove PIN');
    } finally {
      setSavingPin(false);
    }
  };

  // Save organization
  const handleSaveOrganization = async () => {
    if (!organization?.id) return;

    setSaving(true);
    try {
      // Use updateOrganization to update both DB and auth context state
      await updateOrganization({
        name: orgName,
        slug: orgSlug,
        timezone,
        currency,
        tax_settings: { default_rate: parseFloat(taxRate) },
      });

      showToast('success', 'Organization settings saved');
    } catch (error: any) {
      console.error('Error updating organization:', error);
      showToast('error', error.message || 'Failed to update organization');
    } finally {
      setSaving(false);
    }
  };

  // Save AI settings
  const handleSaveAiSettings = async () => {
    if (!organization?.id) return;

    setSavingAiSettings(true);
    try {
      // Get current settings and merge with AI settings
      const currentSettings = organization.settings || {};
      const newSettings = {
        ...currentSettings,
        ai_upsell_enabled: aiEnabled,
        openai_api_key: aiApiKey || undefined,
      };

      // Use updateOrganization to update both DB and auth context state
      await updateOrganization({ settings: newSettings });

      showToast('success', 'AI settings saved successfully');
    } catch (error: any) {
      console.error('Error updating AI settings:', error);
      showToast('error', error.message || 'Failed to update AI settings');
    } finally {
      setSavingAiSettings(false);
    }
  };

  // Upload organization logo
  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !organization?.id) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      showToast('error', 'Please upload a JPG, PNG, or SVG file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      showToast('error', 'File size must be less than 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      // Create unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${organization.id}/logo-${Date.now()}.${fileExt}`;

      // Delete old logo if exists
      if (logoUrl) {
        const oldPath = logoUrl.split('/logos/')[1];
        if (oldPath) {
          await supabase.storage.from('logos').remove([oldPath]);
        }
      }

      // Upload new logo
      const { error: uploadError } = await supabase.storage
        .from('logos')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('logos')
        .getPublicUrl(fileName);

      // Update organization with new logo URL (this also updates the auth context state)
      await updateOrganization({ logo_url: publicUrl });

      setLogoUrl(publicUrl);
      showToast('success', 'Logo uploaded successfully');
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      showToast('error', error.message || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Remove organization logo
  const handleRemoveLogo = async () => {
    if (!organization?.id || !logoUrl) return;

    setUploadingLogo(true);
    try {
      // Delete from storage
      const path = logoUrl.split('/logos/')[1];
      if (path) {
        await supabase.storage.from('logos').remove([path]);
      }

      // Update organization (this also updates the auth context state)
      await updateOrganization({ logo_url: null });

      setLogoUrl(null);
      showToast('success', 'Logo removed');
    } catch (error: any) {
      console.error('Error removing logo:', error);
      showToast('error', error.message || 'Failed to remove logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Save appearance
  const handleSaveAppearance = async () => {
    console.log('[Settings] handleSaveAppearance called with accentColor:', accentColor);
    setSaving(true);
    try {
      const newAppearance = {
        theme: selectedTheme,
        accentColor,
        font: selectedFont,
      };

      if (appearanceScope === 'organization') {
        await updateOrgAppearance.mutateAsync(newAppearance);
        showToast('success', 'Organization appearance settings saved');
      } else {
        if (!selectedAppearanceLocationId) {
          showToast('error', 'Please select a location');
          setSaving(false);
          return;
        }
        await updateLocationAppearance.mutateAsync({
          appearance: newAppearance,
          locationId: selectedAppearanceLocationId,
        });
        const locationName = locations.find(l => l.id === selectedAppearanceLocationId)?.name || 'Location';
        showToast('success', `${locationName} appearance settings saved`);
      }

      // Also apply immediately via theme provider
      setTheme(selectedTheme);
      applyAccentColor(accentColor);
      const fontConfig = SYSTEM_FONTS.find(f => f.value === selectedFont);
      if (fontConfig) {
        document.documentElement.style.setProperty('--system-font', fontConfig.fontFamily);
      }
    } catch (error: any) {
      console.error('Error saving appearance:', error);
      showToast('error', error.message || 'Failed to save appearance settings');
    } finally {
      setSaving(false);
    }
  };

  // Clear location appearance override
  const handleClearLocationOverride = async () => {
    if (!selectedAppearanceLocationId) return;
    try {
      await clearLocationAppearance.mutateAsync(selectedAppearanceLocationId);
      showToast('success', 'Location appearance override removed');
    } catch (error: any) {
      console.error('Error clearing location override:', error);
      showToast('error', error.message || 'Failed to remove location override');
    }
  };

  const header = (
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-2xl font-display font-semibold tracking-tight">
          Settings
        </h1>
        <p className="text-sm text-muted-foreground">
          Manage your account and application preferences
        </p>
      </div>
      {/* Toast notification */}
      {toast && (
        <div
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium animate-in slide-in-from-top-2',
            toast.type === 'success' && 'bg-green-500/10 text-green-600 border border-green-500/20',
            toast.type === 'error' && 'bg-red-500/10 text-red-600 border border-red-500/20'
          )}
        >
          {toast.type === 'success' ? (
            <Check className="h-4 w-4" />
          ) : (
            <AlertCircle className="h-4 w-4" />
          )}
          {toast.message}
        </div>
      )}
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'profile':
        return (
          <div className="space-y-8">
            {/* Profile Information */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold">Profile Information</h3>
                <p className="text-sm text-muted-foreground">
                  Update your personal details and profile picture
                </p>
              </div>

              <div className="flex items-center gap-6">
                <Avatar className="h-20 w-20">
                  <AvatarFallback className="text-xl bg-primary/10 text-primary">
                    {userInitials || 'BP'}
                  </AvatarFallback>
                </Avatar>
                <div className="space-y-2">
                  <Button variant="outline" className="gap-2">
                    <Upload className="h-4 w-4" />
                    Upload Photo
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    JPG, PNG or GIF. Max 2MB.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Enter first name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Enter last name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="bg-muted"
                />
                <p className="text-xs text-muted-foreground">
                  Contact support to change your email address
                </p>
              </div>

              <div className="space-y-2">
                <Label>Role</Label>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="capitalize">
                    {authLoading ? 'Loading...' : (profile?.role || 'Unknown')}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {!authLoading && profile?.role === 'superadmin' && 'Full system access'}
                    {!authLoading && profile?.role === 'owner' && 'Full organization access'}
                    {!authLoading && profile?.role === 'admin' && 'Administrative access'}
                    {!authLoading && profile?.role === 'manager' && 'Management access'}
                    {!authLoading && profile?.role === 'employee' && 'Basic POS access'}
                  </span>
                </div>
              </div>

              <Button 
                variant="blaze" 
                className="gap-2" 
                onClick={handleSaveProfile}
                disabled={saving}
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save Profile
              </Button>
            </div>

            {/* Change Password */}
            <div className="border-t pt-8 space-y-6">
              <div>
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Lock className="h-5 w-5" />
                  Change Password
                </h3>
                <p className="text-sm text-muted-foreground">
                  Update your password to keep your account secure
                </p>
              </div>

              <div className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showCurrentPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showNewPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Must be at least 8 characters
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      className={cn(
                        'pr-10',
                        confirmPassword && newPassword !== confirmPassword && 'border-red-500'
                      )}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {confirmPassword && newPassword !== confirmPassword && (
                    <p className="text-xs text-red-500">Passwords do not match</p>
                  )}
                </div>

                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={handleChangePassword}
                  disabled={changingPassword || !newPassword || newPassword !== confirmPassword}
                >
                  {changingPassword ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Lock className="h-4 w-4" />
                  )}
                  Change Password
                </Button>
              </div>
            </div>

            {/* Manager PIN */}
            <div className="border-t pt-8 space-y-6">
              <div>
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <KeyRound className="h-5 w-5" />
                  Manager PIN
                </h3>
                <p className="text-sm text-muted-foreground">
                  Set a 6-digit PIN for approving voids, refunds, and other manager actions
                </p>
              </div>

              {hasExistingPin && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 text-green-600 border border-green-500/20">
                    <Check className="h-4 w-4" />
                    <span className="text-sm font-medium">PIN is set</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    For security, your PIN cannot be revealed. If you forgot your PIN, you can reset it below by verifying your password.
                  </p>
                </div>
              )}

              {/* PIN Reset with Password Verification */}
              {hasExistingPin && !pinResetVerified && (
                <div className="p-4 border rounded-lg bg-muted/50 space-y-3">
                  <div className="flex items-center gap-2">
                    <Lock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Forgot your PIN?</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Verify your password to reset your PIN.
                  </p>
                  <div className="flex gap-2 items-end">
                    <div className="space-y-1">
                      <Label htmlFor="pinResetPassword" className="text-xs">Password</Label>
                      <Input
                        id="pinResetPassword"
                        type="password"
                        value={pinResetPassword}
                        onChange={(e) => setPinResetPassword(e.target.value)}
                        placeholder="Enter your password"
                        className="max-w-[200px]"
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleVerifyPasswordForPinReset}
                      disabled={verifyingPassword || !pinResetPassword}
                    >
                      {verifyingPassword ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Verify'
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Show PIN form if no existing PIN, or after password verification */}
              {(!hasExistingPin || pinResetVerified) && (
                <div className="space-y-4 max-w-md">
                  {pinResetVerified && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-500/10 text-blue-600 border border-blue-500/20">
                      <Check className="h-4 w-4" />
                      <span className="text-sm font-medium">Password verified. Enter your new PIN below.</span>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="newPin">{hasExistingPin ? 'New PIN' : 'PIN'}</Label>
                    <Input
                      id="newPin"
                      type="password"
                      inputMode="numeric"
                      maxLength={6}
                      value={newPin}
                      onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
                      placeholder="Enter 6-digit PIN"
                      className="max-w-[180px]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPin">Confirm PIN</Label>
                    <Input
                      id="confirmPin"
                      type="password"
                      inputMode="numeric"
                      maxLength={6}
                      value={confirmPin}
                      onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
                      placeholder="Confirm PIN"
                      className={cn(
                        'max-w-[180px]',
                        confirmPin && newPin !== confirmPin && 'border-red-500'
                      )}
                    />
                    {confirmPin && newPin !== confirmPin && (
                      <p className="text-xs text-red-500">PINs do not match</p>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="gap-2"
                      onClick={async () => {
                        await handleSavePin();
                        if (pinResetVerified) {
                          setPinResetVerified(false);
                          setPinResetPassword('');
                        }
                      }}
                      disabled={savingPin || newPin.length !== 6 || newPin !== confirmPin}
                    >
                      {savingPin ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <KeyRound className="h-4 w-4" />
                      )}
                      {hasExistingPin ? 'Update PIN' : 'Set PIN'}
                    </Button>
                    {hasExistingPin && !pinResetVerified && (
                      <Button
                        variant="ghost"
                        className="gap-2 text-destructive hover:text-destructive"
                        onClick={handleRemovePin}
                        disabled={savingPin}
                      >
                        <Trash2 className="h-4 w-4" />
                        Remove PIN
                      </Button>
                    )}
                    {pinResetVerified && (
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setPinResetVerified(false);
                          setPinResetPassword('');
                          setNewPin('');
                          setConfirmPin('');
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Remove PIN option when PIN exists and not in reset mode */}
              {hasExistingPin && !pinResetVerified && (
                <div className="pt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-2 text-destructive hover:text-destructive"
                    onClick={handleRemovePin}
                    disabled={savingPin}
                  >
                    <Trash2 className="h-4 w-4" />
                    Remove PIN
                  </Button>
                </div>
              )}
            </div>
          </div>
        );

      case 'organization':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold">Organization Settings</h3>
              <p className="text-sm text-muted-foreground">
                Manage your business details and branding
              </p>
            </div>

            {/* Logo Upload Section */}
            <div className="space-y-4">
              <Label>Organization Logo</Label>
              <div className="flex items-center gap-6">
                <div className="relative w-24 h-24 rounded-xl border-2 border-dashed border-muted-foreground/30 flex items-center justify-center overflow-hidden bg-muted/30">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt="Organization logo"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <Building2 className="h-10 w-10 text-muted-foreground/50" />
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="gap-2"
                      disabled={uploadingLogo}
                      onClick={() => document.getElementById('logo-upload')?.click()}
                    >
                      {uploadingLogo ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4" />
                      )}
                      Upload Logo
                    </Button>
                    {logoUrl && (
                      <Button
                        variant="outline"
                        className="gap-2 text-destructive hover:text-destructive"
                        disabled={uploadingLogo}
                        onClick={handleRemoveLogo}
                      >
                        <Trash2 className="h-4 w-4" />
                        Remove
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    JPG, PNG, or SVG. Max 2MB. This logo will appear in the sidebar.
                  </p>
                  <input
                    id="logo-upload"
                    type="file"
                    accept="image/jpeg,image/png,image/svg+xml"
                    className="hidden"
                    onChange={handleLogoUpload}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="orgName">Organization Name</Label>
              <Input
                id="orgName"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder="Enter organization name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="orgSlug">URL Slug</Label>
              <div className="flex items-center gap-2">
                <code className="px-3 py-2 bg-muted rounded-md text-sm font-medium">
                  {orgSlug || 'not-set'}<span className="text-muted-foreground">.blazepos.app</span>
                </code>
                {isSuperadmin ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPendingSlug(orgSlug);
                      setSlugChangeDialogOpen(true);
                    }}
                  >
                    <Edit2 className="h-3 w-3 mr-1" />
                    Change
                  </Button>
                ) : (
                  <Lock className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {isSuperadmin 
                  ? 'Your login URL. Click "Change" to modify (affects all users).'
                  : 'Contact your administrator to change the URL slug.'
                }
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="timezone">Timezone</Label>
                <select
                  id="timezone"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="America/Jamaica">Eastern Standard Time (Jamaica)</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="currency">Currency</Label>
                <select
                  id="currency"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="JMD">JMD (J$)</option>
                  <option value="USD">USD ($)</option>
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="taxRate">Default Tax Rate</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="taxRate"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={taxRate}
                  onChange={(e) => setTaxRate(e.target.value)}
                  className="max-w-[120px]"
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
              <p className="text-xs text-muted-foreground">
                This will be applied to all taxable items by default
              </p>
            </div>

            {/* Inventory Accounting Settings */}
            <div className="border-t pt-6 mt-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-green-500" />
                  Inventory Accounting
                </h3>
                <p className="text-sm text-muted-foreground">
                  Configure how inventory costs are calculated
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="costMethod">Cost Accounting Method</Label>
                  <Select
                    value={organization?.cost_method || 'average'}
                    onValueChange={async (value) => {
                      if (!isOwner) return;
                      try {
                        await supabase
                          .from('organizations')
                          .update({ cost_method: value })
                          .eq('id', organization?.id);
                        showToast('success', 'Cost method updated');
                      } catch (error) {
                        showToast('error', 'Failed to update cost method');
                      }
                    }}
                    disabled={!isOwner}
                  >
                    <SelectTrigger id="costMethod">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="average">
                        <div className="flex flex-col items-start">
                          <span>Average Cost</span>
                          <span className="text-xs text-muted-foreground">Rolling average across all locations</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="fifo">
                        <div className="flex flex-col items-start">
                          <span>FIFO (First In, First Out)</span>
                          <span className="text-xs text-muted-foreground">Track individual lot costs</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Average Cost is simpler; FIFO is more accurate for perishables and varying costs.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="costControl">Cost Control for Zero Inventory</Label>
                  <Select
                    value={organization?.cost_control_method || 'default_cost'}
                    onValueChange={async (value) => {
                      if (!isOwner) return;
                      try {
                        await supabase
                          .from('organizations')
                          .update({ cost_control_method: value })
                          .eq('id', organization?.id);
                        showToast('success', 'Cost control setting updated');
                      } catch (error) {
                        showToast('error', 'Failed to update cost control');
                      }
                    }}
                    disabled={!isOwner}
                  >
                    <SelectTrigger id="costControl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default_cost">Use Default Cost</SelectItem>
                      <SelectItem value="last_recorded_cost">Use Last Recorded Cost</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Determines cost assignment when stock is zero or negative.
                  </p>
                </div>

                <div className="p-3 rounded-lg bg-muted/50 text-sm">
                  <p className="font-medium mb-1">Cost Method Comparison:</p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1">
                    <li><strong>Average Cost:</strong> Best for uniform products, simpler tracking</li>
                    <li><strong>FIFO:</strong> Best for perishables, fluctuating costs, accurate COGS</li>
                  </ul>
                </div>
              </div>
            </div>

            <Button 
              variant="blaze" 
              className="gap-2"
              onClick={handleSaveOrganization}
              disabled={saving || !isOwner}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save Organization Settings
            </Button>

            {/* AI Settings Section - Only visible to owners/superadmins */}
            {isOwner && (
              <div className="border-t pt-6 mt-6">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-amber-500" />
                    AI-Enhanced Upsells
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Enable AI-powered product suggestions for smarter upselling
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="p-4 rounded-xl border bg-gradient-to-r from-amber-500/5 to-orange-500/5">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium">Enable AI Suggestions</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          Use OpenAI to generate intelligent product pairing suggestions based on your menu
                        </p>
                      </div>
                      <Switch
                        checked={aiEnabled}
                        onCheckedChange={setAiEnabled}
                      />
                    </div>
                  </div>

                  {aiEnabled && (
                    <div className="space-y-4 animate-in slide-in-from-top-2">
                      <div className="space-y-2">
                        <Label htmlFor="aiApiKey">OpenAI API Key</Label>
                        <div className="relative">
                          <Input
                            id="aiApiKey"
                            type={showAiApiKey ? 'text' : 'password'}
                            value={aiApiKey}
                            onChange={(e) => setAiApiKey(e.target.value)}
                            placeholder="sk-..."
                            className="pr-10"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                            onClick={() => setShowAiApiKey(!showAiApiKey)}
                          >
                            {showAiApiKey ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Get your API key from{' '}
                          <a
                            href="https://platform.openai.com/api-keys"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary underline"
                          >
                            OpenAI Dashboard
                          </a>
                          . Your key is encrypted and stored securely.
                        </p>
                      </div>

                      <div className="p-3 rounded-lg bg-muted/50 text-sm">
                        <p className="font-medium mb-1">How it works:</p>
                        <ul className="list-disc list-inside text-muted-foreground space-y-1">
                          <li>AI analyzes your menu items and descriptions</li>
                          <li>Suggests intelligent product pairings based on culinary knowledge</li>
                          <li>Complements your manual rules and order history patterns</li>
                          <li>API costs are billed directly to your OpenAI account</li>
                        </ul>
                      </div>
                    </div>
                  )}

                  <Button 
                    variant="outline" 
                    className="gap-2"
                    onClick={handleSaveAiSettings}
                    disabled={savingAiSettings}
                  >
                    {savingAiSettings ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    Save AI Settings
                  </Button>
                </div>
              </div>
            )}

            {/* API Keys */}
            <div className="border-t pt-6">
              <ApiKeysSection showToast={showToast} />
            </div>
          </div>
        );

      case 'security':
        return (
          <SecuritySection />
        );

      case 'appearance':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold">Appearance</h3>
              <p className="text-sm text-muted-foreground">
                Customize the look and feel of your dashboard
              </p>
            </div>

            {/* Scope Selector - Only show for primary_admin/superadmin */}
            {canEditOrgSettings && (
              <div className="space-y-4">
                <Label>Apply Settings To</Label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setAppearanceScope('organization')}
                    className={cn(
                      'flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all',
                      appearanceScope === 'organization'
                        ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                        : 'border-transparent bg-muted hover:border-muted-foreground/20'
                    )}
                  >
                    <Building2 className="h-6 w-6" />
                    <span className="text-sm font-medium">Organization-wide</span>
                    <p className="text-xs text-muted-foreground text-center">
                      Applies to all locations by default
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAppearanceScope('location');
                      if (!selectedAppearanceLocationId && locations.length > 0) {
                        setSelectedAppearanceLocationId(locations[0].id);
                      }
                    }}
                    disabled={locations.length === 0}
                    className={cn(
                      'flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all',
                      appearanceScope === 'location'
                        ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                        : 'border-transparent bg-muted hover:border-muted-foreground/20',
                      locations.length === 0 && 'opacity-50 cursor-not-allowed'
                    )}
                  >
                    <MapPin className="h-6 w-6" />
                    <span className="text-sm font-medium">Specific Location</span>
                    <p className="text-xs text-muted-foreground text-center">
                      Override for one location only
                    </p>
                  </button>
                </div>
                {appearanceScope === 'location' && locations.length > 0 && (
                  <div className="space-y-2">
                    <Label>Select Location</Label>
                    <Select
                      value={selectedAppearanceLocationId || ''}
                      onValueChange={setSelectedAppearanceLocationId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a location" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((loc) => (
                          <SelectItem key={loc.id} value={loc.id}>
                            {loc.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {hasLocationOverride && appearanceScope === 'location' && selectedAppearanceLocationId && (
                  <div className="flex items-center justify-between p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <span className="text-sm text-amber-600">
                      This location has custom appearance settings
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleClearLocationOverride}
                      className="text-amber-600 hover:text-amber-700"
                    >
                      Remove Override
                    </Button>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-4">
              <Label>Theme</Label>
              <div className="grid grid-cols-3 gap-4">
                {(['light', 'dark', 'system'] as const).map((themeOption) => (
                  <button
                    key={themeOption}
                    type="button"
                    onClick={() => setSelectedTheme(themeOption)}
                    className={cn(
                      'flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all',
                      selectedTheme === themeOption
                        ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                        : 'border-transparent bg-muted hover:border-muted-foreground/20'
                    )}
                  >
                    <div
                      className={cn(
                        'w-16 h-10 rounded-lg relative overflow-hidden',
                        themeOption === 'light' && 'bg-white border shadow-sm',
                        themeOption === 'dark' && 'bg-zinc-900 border border-zinc-700',
                        themeOption === 'system' && 'border'
                      )}
                    >
                      {themeOption === 'system' && (
                        <>
                          <div className="absolute inset-0 w-1/2 bg-white" />
                          <div className="absolute inset-0 left-1/2 bg-zinc-900" />
                        </>
                      )}
                    </div>
                    <span className="text-sm font-medium capitalize">{themeOption}</span>
                    {selectedTheme === themeOption && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <Label>Accent Color</Label>
              <div className="flex gap-3">
                {[
                  { color: '#f97316', name: 'Orange' },
                  { color: '#3b82f6', name: 'Blue' },
                  { color: '#22c55e', name: 'Green' },
                  { color: '#a855f7', name: 'Purple' },
                  { color: '#ec4899', name: 'Pink' },
                  { color: '#14b8a6', name: 'Teal' },
                ].map((option) => (
                  <button
                    key={option.color}
                    type="button"
                    onClick={() => setAccentColor(option.color)}
                    className={cn(
                      'w-10 h-10 rounded-full ring-2 ring-offset-2 ring-offset-background transition-all relative',
                      accentColor === option.color ? 'ring-foreground' : 'ring-transparent hover:ring-muted'
                    )}
                    style={{ backgroundColor: option.color }}
                    title={option.name}
                  >
                    {accentColor === option.color && (
                      <Check className="h-4 w-4 text-white absolute inset-0 m-auto" />
                    )}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Choose an accent color to personalize your dashboard
              </p>
            </div>

            <div className="space-y-4">
              <Label>Font</Label>
              <div className="grid grid-cols-2 gap-3">
                {SYSTEM_FONTS.map((font) => (
                  <button
                    key={font.value}
                    type="button"
                    onClick={() => setSelectedFont(font.value)}
                    className={cn(
                      'relative flex flex-col items-start gap-1 p-4 rounded-xl border-2 transition-all text-left',
                      selectedFont === font.value
                        ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                        : 'border-transparent bg-muted hover:border-muted-foreground/20'
                    )}
                  >
                    <span
                      className="text-sm font-medium"
                      style={{ fontFamily: font.fontFamily }}
                    >
                      {font.label}
                    </span>
                    <span
                      className="text-xs text-muted-foreground"
                      style={{ fontFamily: font.fontFamily }}
                    >
                      The quick brown fox
                    </span>
                    {selectedFont === font.value && (
                      <Check className="h-4 w-4 text-primary absolute top-2 right-2" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            <Button
              variant="blaze"
              className="gap-2"
              onClick={handleSaveAppearance}
              disabled={saving || updateOrgAppearance.isPending || updateLocationAppearance.isPending}
            >
              <Save className="h-4 w-4" />
              Save Appearance
            </Button>
          </div>
        );

      case 'billing':
        return <BillingSection showToast={showToast} />;

      case 'notifications':
        const notificationOptions = [
          { id: 'newOrders' as const, label: 'New Orders', description: 'Get notified when new orders come in' },
          { id: 'lowStockAlerts' as const, label: 'Low Stock Alerts', description: 'Receive alerts when inventory is running low' },
          { id: 'dailyReports' as const, label: 'Daily Reports', description: 'Receive daily sales summary emails' },
          { id: 'marketingUpdates' as const, label: 'Marketing Updates', description: 'Tips and updates from BlazePOS' },
        ];

        if (notificationPrefsLoading) {
          return (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          );
        }

        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Notification Preferences</h3>
                <p className="text-sm text-muted-foreground">
                  Choose how and when you want to be notified
                </p>
              </div>
              {notificationPrefsSaving && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </div>
              )}
            </div>

            <div className="space-y-4">
              {notificationOptions.map((notification) => (
                <div key={notification.id} className="flex items-center justify-between p-4 rounded-xl border">
                  <div>
                    <h4 className="font-medium">{notification.label}</h4>
                    <p className="text-sm text-muted-foreground">{notification.description}</p>
                  </div>
                  <Switch
                    checked={notificationPreferences[notification.id]}
                    onCheckedChange={(checked) => setNotificationPreference(notification.id, checked)}
                  />
                </div>
              ))}
            </div>

            <p className="text-xs text-muted-foreground">
              Changes are saved automatically.
            </p>
          </div>
        );

      case 'payments':
        const movePaymentType = (index: number, direction: 'up' | 'down') => {
          const newIndex = direction === 'up' ? index - 1 : index + 1;
          if (newIndex < 0 || newIndex >= paymentTypes.length) return;
          
          const reordered = [...paymentTypes];
          const [removed] = reordered.splice(index, 1);
          reordered.splice(newIndex, 0, removed);
          
          updateSortOrders.mutate(reordered.map(pt => pt.id));
        };

        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Payment Types</h3>
                <p className="text-sm text-muted-foreground">
                  Configure custom payment methods. The primary payment type (⭐) will be highlighted orange on the POS.
                </p>
              </div>
              <Button
                variant="blaze"
                className="gap-2"
                onClick={() => {
                  setEditingPaymentType(null);
                  setPaymentTypeForm({ name: '', code: '', is_cash_equivalent: false });
                  setPaymentTypeDialogOpen(true);
                }}
              >
                <Plus className="h-4 w-4" />
                Add Payment Type
              </Button>
            </div>

            {paymentTypesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : paymentTypes.length === 0 ? (
              <div className="text-center py-12 border rounded-xl bg-muted/30">
                <DollarSign className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h4 className="font-medium">No Payment Types</h4>
                <p className="text-sm text-muted-foreground mt-1 mb-4">
                  Add custom payment methods for your business
                </p>
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={() => {
                    setEditingPaymentType(null);
                    setPaymentTypeForm({ name: '', code: '', is_cash_equivalent: false });
                    setPaymentTypeDialogOpen(true);
                  }}
                >
                  <Plus className="h-4 w-4" />
                  Add First Payment Type
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {paymentTypes.map((pt, index) => (
                  <div
                    key={pt.id}
                    className={cn(
                      "flex items-center justify-between p-4 rounded-xl border bg-card transition-all",
                      pt.is_primary && "ring-2 ring-orange-500/50 border-orange-500/30 bg-orange-500/5"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      {/* Reorder Controls */}
                      <div className="flex flex-col gap-0.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5"
                          disabled={index === 0 || updateSortOrders.isPending}
                          onClick={() => movePaymentType(index, 'up')}
                        >
                          <ArrowUp className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5"
                          disabled={index === paymentTypes.length - 1 || updateSortOrders.isPending}
                          onClick={() => movePaymentType(index, 'down')}
                        >
                          <ArrowDown className="h-3 w-3" />
                        </Button>
                      </div>
                      
                      <div className={cn(
                        "p-2 rounded-lg",
                        pt.is_primary ? "bg-orange-500/10" : pt.is_cash_equivalent ? "bg-green-500/10" : "bg-blue-500/10"
                      )}>
                        {pt.is_cash_equivalent ? (
                          <Banknote className={cn("h-5 w-5", pt.is_primary ? "text-orange-600" : "text-green-600")} />
                        ) : (
                          <CreditCard className={cn("h-5 w-5", pt.is_primary ? "text-orange-600" : "text-blue-600")} />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{pt.name}</p>
                          {pt.is_primary && (
                            <Badge className="bg-orange-500 hover:bg-orange-600 text-xs">
                              Primary
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Code: {pt.code}
                          {pt.is_cash_equivalent && (
                            <Badge variant="secondary" className="ml-2 text-xs">
                              Cash Equivalent
                            </Badge>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Set Primary Button */}
                      <Button
                        variant={pt.is_primary ? "default" : "ghost"}
                        size="icon"
                        className={cn(
                          pt.is_primary && "bg-orange-500 hover:bg-orange-600"
                        )}
                        onClick={() => {
                          if (!pt.is_primary) {
                            setPrimaryPaymentType.mutate(pt.id);
                          }
                        }}
                        disabled={setPrimaryPaymentType.isPending}
                        title={pt.is_primary ? "Primary payment type" : "Set as primary"}
                      >
                        <Star className={cn("h-4 w-4", pt.is_primary && "fill-current")} />
                      </Button>
                      <Switch
                        checked={pt.is_active ?? false}
                        onCheckedChange={(checked) => {
                          updatePaymentType.mutate({
                            id: pt.id,
                            updates: { is_active: checked },
                          });
                        }}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setEditingPaymentType(pt);
                          setPaymentTypeForm({
                            name: pt.name,
                            code: pt.code,
                            is_cash_equivalent: pt.is_cash_equivalent ?? false,
                          });
                          setPaymentTypeDialogOpen(true);
                        }}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => {
                          if (confirm(`Delete "${pt.name}"?`)) {
                            deletePaymentType.mutate(pt.id);
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Help Text */}
            <div className="p-4 rounded-xl border bg-muted/30">
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <Star className="h-4 w-4 text-orange-500" />
                Button Order & Primary
              </h4>
              <p className="text-sm text-muted-foreground">
                Use the arrows to reorder payment buttons on the POS screen. 
                Click the star to set a payment type as primary — it will appear as the highlighted orange button.
                Payment buttons appear on the POS in the order shown here.
              </p>
            </div>
          </div>
        );

      case 'team':
        const handleToggleTeamMemberActive = async (member: TeamMember, isActive: boolean) => {
          setTogglingActive(member.id);
          try {
            await toggleStaffActive.mutateAsync({ id: member.id, isActive });
            showToast('success', `${member.first_name} ${member.last_name} is now ${isActive ? 'active' : 'inactive'}`);
          } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to update status';
            showToast('error', errorMessage);
          } finally {
            setTogglingActive(null);
          }
        };

        const getTeamMemberInitials = (member: TeamMember) => {
          return `${member.first_name?.[0] || ''}${member.last_name?.[0] || ''}`.toUpperCase() || '?';
        };

        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Team Overview</h3>
                <p className="text-sm text-muted-foreground">
                  Quick view of your team members. For full management, visit the Team page.
                </p>
              </div>
              <Button variant="blaze" className="gap-2" onClick={() => navigate('/team')}>
                <ExternalLink className="h-4 w-4" />
                Open Team Page
              </Button>
            </div>

            {teamLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Loading team members...</span>
                </div>
              </div>
            ) : teamMembers.length === 0 ? (
              <div className="text-center py-12 border rounded-xl bg-muted/30">
                <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h4 className="font-medium mb-2">No team members yet</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Add team members from the Team page
                </p>
                <Button variant="blaze" className="gap-2" onClick={() => navigate('/team')}>
                  <Plus className="h-4 w-4" />
                  Add Team Members
                </Button>
              </div>
            ) : (
              <>
                <div className="rounded-lg border overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted/50 border-b">
                      <tr>
                        <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                          User
                        </th>
                        <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                          Role
                        </th>
                        <th className="text-center text-sm font-medium text-muted-foreground px-4 py-3">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {teamMembers.slice(0, 10).map((member) => (
                        <tr
                          key={member.id}
                          className={cn(
                            'hover:bg-muted/30 transition-colors',
                            !member.is_active && 'opacity-60'
                          )}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-9 w-9">
                                <AvatarFallback
                                  className={cn(
                                    'font-medium text-xs',
                                    ROLE_COLORS[member.role]?.split(' ')[0] || 'bg-gray-100'
                                  )}
                                >
                                  {getTeamMemberInitials(member)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="min-w-0">
                                <p className="font-medium text-sm truncate">
                                  {member.first_name} {member.last_name}
                                  {member.id === profile?.id && (
                                    <span className="text-xs text-muted-foreground ml-1">(You)</span>
                                  )}
                                </p>
                                <p className="text-xs text-muted-foreground truncate">
                                  {member.email || (member.username ? `@${member.username}` : 'No email')}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge
                              variant="outline"
                              className={cn('capitalize text-xs', ROLE_COLORS[member.role])}
                            >
                              {ROLE_LABELS[member.role] || member.role}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Switch
                              checked={member.is_active}
                              onCheckedChange={(checked) => handleToggleTeamMemberActive(member, checked)}
                              disabled={member.id === profile?.id || togglingActive === member.id}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {teamMembers.length > 10 && (
                  <p className="text-sm text-muted-foreground text-center">
                    Showing 10 of {teamMembers.length} team members.{' '}
                    <button
                      onClick={() => navigate('/team')}
                      className="text-primary hover:underline"
                    >
                      View all
                    </button>
                  </p>
                )}

                <div className="p-4 rounded-xl border bg-muted/30">
                  <h4 className="font-medium mb-2">Need more team management features?</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    The full Team page offers additional features including:
                  </p>
                  <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1 mb-4">
                    <li>Sales targets management</li>
                    <li>Kiosk device setup</li>
                    <li>Outlet/location access control</li>
                    <li>Create new team members</li>
                  </ul>
                  <Button variant="outline" className="gap-2" onClick={() => navigate('/team')}>
                    <ExternalLink className="h-4 w-4" />
                    Go to Team Page
                  </Button>
                </div>
              </>
            )}
          </div>
        );

      case 'locations':
        const handleSaveLocation = async () => {
          setLocationFormError(null);

          if (!locationForm.name) {
            setLocationFormError('Location name is required');
            return;
          }

          try {
            if (editingLocation) {
              await updateLocation.mutateAsync({
                id: editingLocation.id,
                updates: {
                  name: locationForm.name,
                  phone: locationForm.phone || null,
                  email: locationForm.email || null,
                  address: {
                    line1: locationForm.address_line1,
                    city: locationForm.address_city,
                    parish: locationForm.address_parish,
                  },
                },
              });
              showToast('success', 'Location updated');
            } else {
              await createLocation.mutateAsync({
                name: locationForm.name,
                phone: locationForm.phone || null,
                email: locationForm.email || null,
                address: {
                  line1: locationForm.address_line1,
                  city: locationForm.address_city,
                  parish: locationForm.address_parish,
                },
              });
              showToast('success', `${locationForm.name} has been added`);
            }
            setLocationDialogOpen(false);
            setEditingLocation(null);
            setLocationForm({
              name: '',
              phone: '',
              email: '',
              address_line1: '',
              address_city: '',
              address_parish: '',
            });
          } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to save location';
            setLocationFormError(errorMessage);
            showToast('error', errorMessage);
          }
        };

        const openDeleteLocationDialog = (location: Location) => {
          setLocationToDelete(location);
          setDeleteLocationDialogOpen(true);
        };

        const handleDeleteLocationConfirm = async () => {
          if (!locationToDelete) return;

          try {
            await deleteLocation.mutateAsync(locationToDelete.id);
            showToast('success', `${locationToDelete.name} has been deleted`);
            setDeleteLocationDialogOpen(false);
            setLocationToDelete(null);
          } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete location';
            showToast('error', errorMessage);
          }
        };

        const handleToggleLocationActive = async (location: Location, isActive: boolean) => {
          try {
            await updateLocation.mutateAsync({
              id: location.id,
              updates: { is_active: isActive },
            });
            showToast('success', `${location.name} is now ${isActive ? 'active' : 'inactive'}`);
          } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to update location';
            showToast('error', errorMessage);
          }
        };

        const handleSeedData = async (location: Location) => {
          if (!canSeedData) return;

          setSeedingLocationId(location.id);
          try {
            const { data, error } = await supabase.rpc('seed_test_data_for_location', {
              target_location_id: location.id,
            });

            if (error) throw error;

            const result = data as { success: boolean; today_orders?: number; last_week_orders?: number; error?: string };

            if (result.success) {
              const todayOrders = result.today_orders ?? 0;
              const lastWeekOrders = result.last_week_orders ?? 0;
              showToast('success', `Seeded ${todayOrders} orders today and ${lastWeekOrders} last week at ${location.name}`);
            } else {
              throw new Error(result.error || 'Failed to seed data');
            }
          } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to seed test data';
            showToast('error', errorMessage);
          } finally {
            setSeedingLocationId(null);
          }
        };

        const openEditLocationDialog = (location: Location) => {
          setEditingLocation(location);
          const address = location.address as { line1?: string; city?: string; parish?: string; state?: string } | null;
          setLocationForm({
            name: location.name,
            phone: location.phone || '',
            email: location.email || '',
            address_line1: address?.line1 || '',
            address_city: address?.city || '',
            address_parish: address?.parish || address?.state || '',
          });
          setLocationFormError(null);
          setLocationDialogOpen(true);
        };

        const openCreateLocationDialog = () => {
          setEditingLocation(null);
          setLocationForm({
            name: '',
            phone: '',
            email: '',
            address_line1: '',
            address_city: '',
            address_parish: '',
          });
          setLocationFormError(null);
          setLocationDialogOpen(true);
        };

        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Location Management</h3>
                <p className="text-sm text-muted-foreground">
                  Manage your organization's locations and locations
                </p>
              </div>
              <Button variant="blaze" className="gap-2" onClick={openCreateLocationDialog}>
                <Plus className="h-4 w-4" />
                Add Location
              </Button>
            </div>

            {locationsLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Loading locations...</span>
                </div>
              </div>
            ) : locations.length === 0 ? (
              <div className="text-center py-12 border rounded-xl bg-muted/30">
                <MapPin className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h4 className="font-medium mb-2">No locations yet</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Add your first location to get started
                </p>
                <Button variant="blaze" className="gap-2" onClick={openCreateLocationDialog}>
                  <Plus className="h-4 w-4" />
                  Add Location
                </Button>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {locations.map((location) => {
                  const address = location.address as { line1?: string; city?: string; parish?: string; state?: string } | null;
                  return (
                    <div
                      key={location.id}
                      className={cn(
                        'p-5 rounded-xl border bg-card transition-all',
                        !location.is_active && 'opacity-60'
                      )}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            'w-10 h-10 rounded-lg flex items-center justify-center',
                            location.is_active ? 'bg-primary/10' : 'bg-muted'
                          )}>
                            <Building className={cn(
                              'h-5 w-5',
                              location.is_active ? 'text-primary' : 'text-muted-foreground'
                            )} />
                          </div>
                          <div>
                            <h4 className="font-semibold">{location.name}</h4>
                            {(address?.city || address?.parish || address?.state) && (
                              <p className="text-sm text-muted-foreground">
                                {address.city}{address.city && (address.parish || address.state) ? ', ' : ''}{address.parish || address.state}
                              </p>
                            )}
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditLocationDialog(location)}>
                              <Edit2 className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            {canSeedData && (
                              <DropdownMenuItem
                                onClick={() => handleSeedData(location)}
                                disabled={seedingLocationId === location.id}
                              >
                                {seedingLocationId === location.id ? (
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                  <Database className="h-4 w-4 mr-2" />
                                )}
                                Seed Test Data
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={() => openDeleteLocationDialog(location)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      {/* Contact Info */}
                      <div className="space-y-2 mb-4">
                        {location.phone && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Phone className="h-3.5 w-3.5" />
                            {location.phone}
                          </div>
                        )}
                        {location.email && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Mail className="h-3.5 w-3.5" />
                            {location.email}
                          </div>
                        )}
                        {address?.line1 && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <MapPin className="h-3.5 w-3.5" />
                            {address.line1}
                          </div>
                        )}
                      </div>

                      {/* Status Toggle */}
                      <div className="flex items-center justify-between pt-3 border-t">
                        <span className="text-sm font-medium">Active</span>
                        <Switch
                          checked={location.is_active}
                          onCheckedChange={(checked) => handleToggleLocationActive(location, checked)}
                        />
                      </div>

                      {/* FOH Stock Warning Setting */}
                      <div className="pt-3 border-t space-y-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-sm font-medium">Low FOH Stock Behavior</span>
                            <p className="text-xs text-muted-foreground">
                              What happens when cashier sells more than available FOH stock
                            </p>
                          </div>
                        </div>
                        <Select
                          value={(location.settings?.foh_stock_warning_behavior as FohStockWarningBehavior) || 'require_pin'}
                          onValueChange={(value: FohStockWarningBehavior) => {
                            updateLocationSettings.mutate({
                              id: location.id,
                              settings: { foh_stock_warning_behavior: value }
                            });
                          }}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="warn_allow">
                              <div className="flex flex-col items-start">
                                <span>Warn but allow</span>
                                <span className="text-xs text-muted-foreground">Show warning, proceed with sale</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="block_hard">
                              <div className="flex flex-col items-start">
                                <span>Block completely</span>
                                <span className="text-xs text-muted-foreground">Prevent sale until stock is added</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="require_pin">
                              <div className="flex flex-col items-start">
                                <span>Require manager PIN</span>
                                <span className="text-xs text-muted-foreground">Manager must approve to proceed</span>
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Location Dialog */}
            <ResponsiveModal 
              open={locationDialogOpen} 
              onOpenChange={(open) => {
                setLocationDialogOpen(open);
                if (!open) {
                  setEditingLocation(null);
                  setLocationFormError(null);
                }
              }}
            >
              <ResponsiveModalContent maxWidth="md">
                <ResponsiveModalHeader>
                  <ResponsiveModalTitle>
                    {editingLocation ? 'Edit Location' : 'Add New Location'}
                  </ResponsiveModalTitle>
                  <ResponsiveModalDescription>
                    {editingLocation
                      ? 'Update location details'
                      : 'Create a new location for your organization'}
                  </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <ResponsiveModalBody>
                  <div className="grid gap-4">
                    {locationFormError && (
                      <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-950/20 rounded-lg">
                        {locationFormError}
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="locationName">Location Name *</Label>
                      <Input
                        id="locationName"
                        value={locationForm.name}
                        onChange={(e) => setLocationForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g., Downtown Store"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="locationPhone">Phone</Label>
                        <Input
                          id="locationPhone"
                          value={locationForm.phone}
                          onChange={(e) => setLocationForm(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="(555) 123-4567"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="locationEmail">Email</Label>
                        <Input
                          id="locationEmail"
                          type="email"
                          value={locationForm.email}
                          onChange={(e) => setLocationForm(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="store@example.com"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="locationAddress">Address</Label>
                      <Input
                        id="locationAddress"
                        value={locationForm.address_line1}
                        onChange={(e) => setLocationForm(prev => ({ ...prev, address_line1: e.target.value }))}
                        placeholder="123 Main St"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="locationCity">City</Label>
                        <Input
                          id="locationCity"
                          value={locationForm.address_city}
                          onChange={(e) => setLocationForm(prev => ({ ...prev, address_city: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="locationParish">Parish</Label>
                        <Select
                          value={locationForm.address_parish}
                          onValueChange={(value) => setLocationForm(prev => ({ ...prev, address_parish: value }))}
                        >
                          <SelectTrigger id="locationParish">
                            <SelectValue placeholder="Select parish" />
                          </SelectTrigger>
                          <SelectContent>
                            {JAMAICA_PARISHES.map((parish) => (
                              <SelectItem key={parish} value={parish}>
                                {parish}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </ResponsiveModalBody>

                <ResponsiveModalFooter>
                  <Button
                    variant="outline"
                    onClick={() => setLocationDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="blaze"
                    disabled={createLocation.isPending || updateLocation.isPending}
                    onClick={handleSaveLocation}
                  >
                    {(createLocation.isPending || updateLocation.isPending) && (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    )}
                    {editingLocation ? 'Save Changes' : 'Create Location'}
                  </Button>
                </ResponsiveModalFooter>
              </ResponsiveModalContent>
            </ResponsiveModal>

            {/* Delete Location Confirmation Dialog */}
            <ResponsiveModal 
              open={deleteLocationDialogOpen} 
              onOpenChange={(open) => {
                setDeleteLocationDialogOpen(open);
                if (!open) setLocationToDelete(null);
              }}
            >
              <ResponsiveModalContent maxWidth="sm">
                <ResponsiveModalHeader>
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                    </div>
                    <div>
                      <ResponsiveModalTitle>Delete Location</ResponsiveModalTitle>
                      <ResponsiveModalDescription>
                        This action cannot be undone
                      </ResponsiveModalDescription>
                    </div>
                  </div>
                </ResponsiveModalHeader>

                <ResponsiveModalBody>
                  <p className="text-sm text-muted-foreground">
                    Are you sure you want to delete <span className="font-semibold text-foreground">{locationToDelete?.name}</span>? 
                    This will permanently remove the location and cannot be recovered.
                  </p>
                </ResponsiveModalBody>

                <ResponsiveModalFooter>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setDeleteLocationDialogOpen(false);
                      setLocationToDelete(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={handleDeleteLocationConfirm}
                    disabled={deleteLocation.isPending}
                  >
                    {deleteLocation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                    Delete Location
                  </Button>
                </ResponsiveModalFooter>
              </ResponsiveModalContent>
            </ResponsiveModal>
          </div>
        );

      case 'hardware':
        return (
          <div className="space-y-8">
            {/* Printers Section */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Printer className="h-5 w-5" />
                  Printers
                </h3>
                <p className="text-sm text-muted-foreground">
                  Configure receipt and preparation printers
                </p>
              </div>

              {!isDesktopApp && (
                <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-amber-700 dark:text-amber-400">
                        Desktop App Required
                      </h4>
                      <p className="text-sm text-amber-600 dark:text-amber-500 mt-1">
                        Advanced printer configuration requires the BlazePOS desktop app.
                        In the web app, printing uses your browser's print dialog.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {/* Receipt Printer */}
                <div className="p-4 rounded-lg border bg-card">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="font-medium">Receipt Printer</h4>
                      <p className="text-sm text-muted-foreground">
                        Used for customer receipts
                      </p>
                    </div>
                    {isDesktopApp && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={refreshPrinters}
                        disabled={printersLoading}
                        className="gap-2"
                      >
                        <RefreshCw className={cn('h-4 w-4', printersLoading && 'animate-spin')} />
                        Refresh
                      </Button>
                    )}
                  </div>

                  {isDesktopApp ? (
                    printers.length > 0 ? (
                      <div className="space-y-3">
                        <Select
                          value={defaultReceiptPrinter || ''}
                          onValueChange={setReceiptPrinter}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select receipt printer..." />
                          </SelectTrigger>
                          <SelectContent>
                            {printers.map((printer) => (
                              <SelectItem key={printer.name} value={printer.name}>
                                <div className="flex items-center gap-2">
                                  <Printer className="h-4 w-4" />
                                  {printer.displayName}
                                  {printer.isDefault && (
                                    <Badge variant="secondary" className="ml-2 text-[10px]">
                                      System Default
                                    </Badge>
                                  )}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {defaultReceiptPrinter && (
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => printTestPage(defaultReceiptPrinter)}
                              className="gap-2"
                            >
                              <Printer className="h-4 w-4" />
                              Print Test Page
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        No printers found. Make sure your printer is connected and turned on.
                      </p>
                    )
                  ) : (
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-muted">
                        <Printer className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Browser Printing</p>
                        <p className="text-xs text-muted-foreground">
                          Receipts will print using your browser's print dialog
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => printTestPage('default')}
                        className="ml-auto gap-2"
                      >
                        <Printer className="h-4 w-4" />
                        Test Print
                      </Button>
                    </div>
                  )}
                </div>

                {/* Preparation Printer */}
                {isDesktopApp && (
                  <div className="p-4 rounded-lg border bg-card">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="font-medium">Preparation Printer</h4>
                        <p className="text-sm text-muted-foreground">
                          Used for order preparation tickets
                        </p>
                      </div>
                    </div>

                    {printers.length > 0 ? (
                      <div className="space-y-3">
                        <Select
                          value={defaultKitchenPrinter || '__none__'}
                          onValueChange={(value) => setKitchenPrinter(value === '__none__' ? '' : value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select preparation printer..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">None (Screen-based display)</SelectItem>
                            {printers.map((printer) => (
                              <SelectItem key={printer.name} value={printer.name}>
                                <div className="flex items-center gap-2">
                                  <Printer className="h-4 w-4" />
                                  {printer.displayName}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {defaultKitchenPrinter && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => printTestPage(defaultKitchenPrinter)}
                            className="gap-2"
                          >
                            <Printer className="h-4 w-4" />
                            Print Test Page
                          </Button>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        No printers found.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Auto-Print Section - Desktop Only */}
            {isDesktopApp && (
              <div className="border-t pt-8 space-y-6">
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Printer className="h-5 w-5" />
                    Auto-Print
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Automatically print receipts without showing a dialog
                  </p>
                </div>

                <div className="p-4 rounded-lg border bg-card">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">Enable Auto-Print</h4>
                      <p className="text-sm text-muted-foreground">
                        When enabled, receipts will print automatically to the default printer without showing the print dialog
                      </p>
                    </div>
                    <Switch
                      checked={localStorage.getItem('blazepos-auto-print') === 'true'}
                      onCheckedChange={(checked) => {
                        localStorage.setItem('blazepos-auto-print', checked ? 'true' : 'false');
                        // Force re-render
                        window.dispatchEvent(new Event('storage'));
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Cash Drawer Section */}
            {isDesktopApp && (
              <div className="border-t pt-8 space-y-6">
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Banknote className="h-5 w-5" />
                    Cash Drawer
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Control your cash drawer
                  </p>
                </div>

                <div className="p-4 rounded-lg border bg-card">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">Open Cash Drawer</h4>
                      <p className="text-sm text-muted-foreground">
                        Manually open the cash drawer connected via your receipt printer
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={openCashDrawer}
                      className="gap-2"
                    >
                      <Banknote className="h-4 w-4" />
                      Open Drawer
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Barcode Scanner Section */}
            <div className="border-t pt-8 space-y-6">
              <div>
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  Barcode Scanner
                </h3>
                <p className="text-sm text-muted-foreground">
                  Configure barcode scanner settings
                </p>
              </div>

              <div className="p-4 rounded-lg border bg-card">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-500/10">
                    <Check className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-medium">Keyboard Wedge Mode</h4>
                    <p className="text-sm text-muted-foreground">
                      Barcode scanners that emulate keyboard input work automatically.
                      Just scan a barcode while the search field is focused.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        const ActiveIcon = settingsSections.find((s) => s.id === activeSection)?.icon || SettingsIcon;
        return (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <ActiveIcon className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="font-medium capitalize">{activeSection} Settings</h3>
            <p className="text-sm text-muted-foreground mt-1">
              This section is coming soon
            </p>
          </div>
        );
    }
  };

  return (
    <DashboardLayout header={header}>
      <div className="flex gap-6">
        {/* Sidebar */}
        <Card className="w-72 shrink-0 h-fit">
          <CardContent className="p-2">
            <nav className="space-y-1">
              {settingsSections
                .filter((section) => {
                  // Filter out admin-only sections for non-admins
                  if ('adminOnly' in section && section.adminOnly && !canEditOrgSettings) return false;
                  // Filter out owner-and-above sections
                  if ('ownerAndAbove' in section && section.ownerAndAbove) {
                    const ownerRoles = ['superadmin', 'primary_admin', 'owner'];
                    if (!ownerRoles.includes(profile?.role || '')) return false;
                  }
                  // Filter out Team, Locations, Payments unless enabled via feature flags
                  if (section.id === 'team' && !flags.showTeamSettings) return false;
                  if (section.id === 'locations' && !flags.showLocationSettings) return false;
                  if (section.id === 'payments' && !flags.showPaymentSettings) return false;
                  return true;
                })
                .map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={cn(
                      'w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors',
                      activeSection === section.id
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{section.label}</p>
                    </div>
                    <ChevronRight
                      className={cn(
                        'h-4 w-4 shrink-0 transition-transform',
                        activeSection === section.id && 'rotate-90'
                      )}
                    />
                  </button>
                );
              })}
            </nav>
          </CardContent>
        </Card>

        {/* Content */}
        <Card className="flex-1">
          <CardContent className="p-6">{renderContent()}</CardContent>
        </Card>
      </div>

      {/* Payment Type Dialog */}
      <ResponsiveModal open={paymentTypeDialogOpen} onOpenChange={setPaymentTypeDialogOpen}>
        <ResponsiveModalContent maxWidth="sm">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>
              {editingPaymentType ? 'Edit Payment Type' : 'Add Payment Type'}
            </ResponsiveModalTitle>
            <ResponsiveModalDescription>
              {editingPaymentType
                ? 'Update the payment type details'
                : 'Create a custom payment method for your POS'}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody>
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="paymentTypeName">Name *</Label>
                <Input
                  id="paymentTypeName"
                  value={paymentTypeForm.name}
                  onChange={(e) => setPaymentTypeForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., EPAY, Rubis Subsidy"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="paymentTypeCode">Code *</Label>
                <Input
                  id="paymentTypeCode"
                  value={paymentTypeForm.code}
                  onChange={(e) => setPaymentTypeForm(prev => ({ 
                    ...prev, 
                    code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_')
                  }))}
                  placeholder="e.g., epay, rubis_subsidy"
                />
                <p className="text-xs text-muted-foreground">
                  Lowercase letters, numbers, and underscores only
                </p>
              </div>

              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div>
                  <Label htmlFor="isCashEquivalent" className="font-medium">Cash Equivalent</Label>
                  <p className="text-sm text-muted-foreground">
                    Counts towards cash drawer balance
                  </p>
                </div>
                <Switch
                  id="isCashEquivalent"
                  checked={paymentTypeForm.is_cash_equivalent}
                  onCheckedChange={(checked) => setPaymentTypeForm(prev => ({ 
                    ...prev, 
                    is_cash_equivalent: checked 
                  }))}
                />
              </div>
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button
              variant="outline"
              onClick={() => setPaymentTypeDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="blaze"
              disabled={!paymentTypeForm.name || !paymentTypeForm.code || savingPaymentType}
              onClick={async () => {
                setSavingPaymentType(true);
                try {
                  if (editingPaymentType) {
                    await updatePaymentType.mutateAsync({
                      id: editingPaymentType.id,
                      updates: {
                        name: paymentTypeForm.name,
                        code: paymentTypeForm.code,
                        is_cash_equivalent: paymentTypeForm.is_cash_equivalent,
                      },
                    });
                    showToast('success', 'Payment type updated');
                  } else {
                    await createPaymentType.mutateAsync({
                      name: paymentTypeForm.name,
                      code: paymentTypeForm.code,
                      is_cash_equivalent: paymentTypeForm.is_cash_equivalent,
                    });
                    showToast('success', 'Payment type created');
                  }
                  setPaymentTypeDialogOpen(false);
                } catch (error: any) {
                  showToast('error', error.message || 'Failed to save payment type');
                } finally {
                  setSavingPaymentType(false);
                }
              }}
            >
              {savingPaymentType && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editingPaymentType ? 'Update' : 'Create'}
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* URL Slug Change Confirmation Dialog */}
      <ResponsiveModal open={slugChangeDialogOpen} onOpenChange={setSlugChangeDialogOpen}>
        <ResponsiveModalContent maxWidth="sm">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Change URL Slug?
            </ResponsiveModalTitle>
            <ResponsiveModalDescription>
              This will change the login URL for all users in this organization.
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>
          <ResponsiveModalBody className="space-y-4">
            <div className="p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <strong>Warning:</strong> Changing the URL slug will:
              </p>
              <ul className="mt-2 text-sm text-amber-700 dark:text-amber-300 list-disc list-inside space-y-1">
                <li>Change the login URL for all staff members</li>
                <li>Break any bookmarked login pages</li>
                <li>Require users to use the new URL to access the system</li>
              </ul>
            </div>
            <div className="space-y-2">
              <Label>Current URL</Label>
              <code className="block px-3 py-2 bg-muted rounded-md text-sm">
                https://{orgSlug}.blazepos.app
              </code>
            </div>
            <div className="space-y-2">
              <Label>New URL Slug</Label>
              <div className="flex items-center gap-0">
                <code className="px-3 py-2 bg-muted rounded-l-md text-sm text-muted-foreground border border-r-0 border-input">
                  https://
                </code>
                <Input
                  value={pendingSlug}
                  onChange={(e) => setPendingSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="your-store"
                  className="rounded-none border-x-0 max-w-[180px]"
                />
                <code className="px-3 py-2 bg-muted rounded-r-md text-sm text-muted-foreground border border-l-0 border-input">
                  .blazepos.app
                </code>
              </div>
              {pendingSlug && pendingSlug !== orgSlug && (
                <p className="text-xs text-primary">
                  New URL: https://{pendingSlug}.blazepos.app
                </p>
              )}
            </div>
          </ResponsiveModalBody>
          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => {
              setPendingSlug('');
              setSlugChangeDialogOpen(false);
            }}>
              Cancel
            </Button>
            <Button
              variant="blaze"
              disabled={!pendingSlug || pendingSlug === orgSlug}
              onClick={() => {
                setOrgSlug(pendingSlug);
                setSlugChangeDialogOpen(false);
                setPendingSlug('');
              }}
            >
              Change URL Slug
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>
    </DashboardLayout>
  );
}
